import type { QueryClient } from "@tanstack/react-query";
import type { ComponentProps } from "react";
import { create } from "zustand";

import type { FeedbackContext, FeedbackKind, FeedbackMine, FeedbackSeverity, FeedbackStatus } from "../api/types";
import type { Badge } from "../components/ui";
import { k, t } from "../i18n/i18n";
import { useUi } from "../state/ui";
import { captureView } from "./capture";
import { currentContext } from "./context";

/** Retours des utilisateurs (registre F39) : fenêtres ouvertes, libellés, champs requis. */

export const FEEDBACK_KEY = ["feedback", "mine"] as const;
export const FEEDBACK_ADMIN_KEY = ["feedback", "admin"] as const;

export const TITLE_MAX = 120;
export const DESCRIPTION_MAX = 4000;
export const DETAIL_MAX = 2000;
export const REPLY_MAX = 4000;

export type FeedbackPane = FeedbackKind | "mine" | "admin";

interface FeedbackState {
  pane: FeedbackPane | null;
  /** Capture de la vue prise à l'ouverture (avant la fenêtre), et contexte de cet instant. */
  capture: Promise<string | null> | null;
  context: FeedbackContext | null;
  /** Champs remplis d'avance (« Signaler cette anomalie » d'un message d'erreur : ce qui s'est passé). */
  prefill: FeedbackPrefill | null;
  /** Menu du bouton « Retours » ouvert, et sur quel bouton : celui de l'accueil ou celui de la barre latérale. */
  menu: FeedbackPlace | null;
}

/** Emplacement du bouton « Retours » : en-tête de l'accueil, barre latérale de l'espace de travail. */
export type FeedbackPlace = "home" | "sidebar";

export interface FeedbackPrefill {
  happened?: string;
}

export const useFeedback = create<FeedbackState>(() => ({ pane: null, capture: null, context: null, prefill: null, menu: null }));

/** Ouvre la fenêtre d'un retour : la vue est copiée et le contexte relevé tout de suite, avant que la fenêtre ne
 *  s'affiche par-dessus. `prefill` : champs remplis d'avance (message d'erreur affiché). */
export function openFeedback(kind: FeedbackKind, queryClient: QueryClient | null, prefill?: FeedbackPrefill): void {
  useFeedback.setState({ pane: kind, capture: captureView(), context: currentContext(queryClient), prefill: prefill ?? null, menu: null });
}

export function openFeedbackPane(pane: "mine" | "admin"): void {
  useFeedback.setState({ pane, capture: null, context: null, prefill: null, menu: null });
}

export function closeFeedback(): void {
  useFeedback.setState({ pane: null, capture: null, context: null, prefill: null });
}

/** Bouton « Retours » visible : celui de l'accueil quand l'accueil est affiché, sinon celui de la barre latérale. */
export function visibleFeedbackPlace(): FeedbackPlace {
  return useUi.getState().welcome ? "home" : "sidebar";
}

/** Ouvre (ou ferme, `null`) le menu du bouton « Retours » visible. */
export function setFeedbackMenu(place: FeedbackPlace | null): void {
  useFeedback.setState({ menu: place });
}

/** Sélecteur du bouton « Retours » d'un emplacement (désignation, bulle d'annonce) : l'accueil couvre l'espace de
 *  travail monté dessous, dont le bouton ne doit pas être désigné à sa place. */
export function launcherSelector(place: FeedbackPlace): string {
  return place === "home" ? '[data-feedback-launcher="home"]' : '[data-feedback-launcher="sidebar"], [data-feedback-launcher="rail"]';
}

/** Nombre montré en pastille sur le bouton « Retours » : réponses non lues, plus les nouveaux retours de toutes les
 *  licences pour une licence d'administration. */
export function waitingCount(summary: Pick<FeedbackMine, "unread" | "admin" | "admin_new"> | undefined): number {
  if (!summary) return 0;
  return (summary.unread ?? 0) + (summary.admin ? (summary.admin_new ?? 0) : 0);
}

/** Pastille au-delà de 99 : « 99+ ». */
export function countLabel(count: number): string {
  return count > 99 ? "99+" : String(count);
}

export const KIND_LABELS: Record<FeedbackKind, string> = {
  evolution: k("Évolution"),
  anomaly: k("Anomalie"),
};

export const SEVERITY_LABELS: Record<FeedbackSeverity, string> = {
  annoying: k("Gênant"),
  blocking: k("Bloquant"),
};

export const STATUS_LABELS: Record<FeedbackStatus, string> = {
  new: k("Nouveau"),
  acknowledged: k("Pris en compte"),
  delivered: k("Corrigé, livré"),
  declined: k("Refusé"),
};

export const STATUS_TONES: Record<FeedbackStatus, NonNullable<ComponentProps<typeof Badge>["tone"]>> = {
  new: "accent",
  acknowledged: "warn",
  delivered: "ok",
  declined: "neutral",
};

export interface FeedbackDraft {
  kind: FeedbackKind;
  title: string;
  description: string;
  doing: string;
  happened: string;
  severity: FeedbackSeverity | null;
}

export type DraftField = "title" | "description" | "happened" | "severity";

/** Premier champ requis qui manque (null : envoi possible) : titre, puis pour une évolution sa description, pour
 *  une anomalie ce qui s'est passé et la gravité. */
export function missingField(draft: FeedbackDraft): DraftField | null {
  if (!draft.title.trim()) return "title";
  if (draft.kind === "evolution") return draft.description.trim() ? null : "description";
  if (!draft.happened.trim()) return "happened";
  return draft.severity ? null : "severity";
}

/** Le brouillon contient-il une saisie (fermeture à confirmer) ? */
export function draftDirty(draft: FeedbackDraft): boolean {
  return Boolean(draft.title.trim() || draft.description.trim() || draft.doing.trim() || draft.happened.trim());
}

/** Lignes du contexte montrées à l'utilisateur : libellé traduit, valeur ; les champs absents sont omis. */
export function contextRows(
  context: FeedbackContext,
  labels: { view: (view: string) => string; level: (level: string) => string; mission: (mission: string) => string },
): { label: string; value: string }[] {
  const rows: { label: string; value: string }[] = [];
  const add = (label: string, value: string | undefined | null) => {
    if (value) rows.push({ label: t(label), value });
  };
  add(k("Vue ouverte"), context.welcome ? t("Accueil") : context.view ? labels.view(context.view) : null);
  add(k("Dossier"), context.exercise_name);
  add(k("Identifiant du dossier"), context.exercise_id);
  add(k("Niveau"), context.level ? labels.level(context.level) : null);
  add(k("Mission"), context.mission ? labels.mission(context.mission) : null);
  add(k("Version"), context.client_build);
  add(k("Langue"), context.language === "en" ? t("Anglais") : context.language === "fr" ? t("Français") : null);
  add(k("Navigateur"), context.user_agent);
  add(k("Écran"), context.screen);
  add(k("Fenêtre"), context.viewport);
  if (context.errors?.length)
    rows.push({ label: t("Erreurs récentes"), value: context.errors.map((e) => e.message).join(" · ") });
  return rows;
}
